<template>
    <main
        class="pft-v2"
        id="js-page-content"
        role="main"
    >
        <portal to="ariane">
            <div class="tbv2-page-title">{{ formTypeName }}</div>
        </portal>

        <header class="pft-hero">
            <div class="pft-hero-text">
                <h1 class="pft-hero-title">{{ formTypeName }}</h1>
                <p class="pft-hero-sub">{{ totalLabel }}</p>
            </div>
            <MultipleListCreate class="pft-multi-list" />
        </header>

        <section v-show="hasData" class="pft-card">
            <TabPosts
                ref="tabPosts"
                :isLoadingPosts="isLoadingPosts"
                :formTypeId="formTypeId"
                @loadpage="onLoadPage"
            />
        </section>

        <div v-if="loaded && !hasData" class="pft-empty">
            <div class="pft-empty-icon">
                <i class="fal fa-inbox"></i>
            </div>
            <h3 class="pft-empty-title">{{ $t('posts.empty.title') }}</h3>
            <p class="pft-empty-text">{{ $t('posts.empty.description') }}</p>
            <router-link to="/post/create/forms" class="pft-empty-btn">
                <i class="fal fa-plus"></i>
                {{ $t('posts.empty.create') }}
            </router-link>
        </div>
    </main>
</template>

<script>
import TabPosts from '@/components/Model/Post/TabPosts'
import { mapActions, mapState } from 'vuex'
import MultipleListCreate from '@/components/UI/MultipleListCreate'

export default {
    name: 'PostsByFormType',
    components: { TabPosts, MultipleListCreate },
    data() {
        return {
            isLoadingPosts: false,
            loaded: false,
            formType: null
        }
    },
    computed: {
        ...mapState('post', {
            config: (state) => state.postsConfig,
            posts: (state) => state.posts
        }),
        hasData() {
            return this.posts && this.posts.data && this.posts.data.length > 0
        },
        formTypeId() {
            return this.$route.params.form_type_id
        },
        formTypeName() {
            if (this.formType && this.formType.name) return this.formType.name
            return this.$t('menu.tickets.list')
        },
        totalLabel() {
            const n = this.posts && this.posts.total ? this.posts.total : 0
            const formatted = new Intl.NumberFormat(this.$i18n.locale).format(n)
            const key = n === 1 ? 'post.page.list_count_one' : 'post.page.list_count_other'
            return this.$t(key, { count: formatted })
        }
    },
    created() {
        // Reset filters and inject form_type_id BEFORE PostsFilters mounts
        this.config.filters = { form_type_id: this.formTypeId }
        this.loadFormType()
    },
    mounted() {
        this.$refs.tabPosts.loadAsyncData()
        this.$nextTick(() => {
            this.loaded = true
        })
    },
    watch: {
        formTypeId() {
            this.loadFormType()
        }
    },
    methods: {
        ...mapActions('form', ['getFormType']),
        loadFormType() {
            if (!this.formTypeId) return
            this.getFormType(this.formTypeId)
                .then((response) => {
                    this.formType = (response && response.data && (response.data.type || response.data)) || null
                })
                .catch(() => {
                    this.formType = null
                })
        },
        onLoadPage() {
            this.isLoadingPosts = true
        }
    }
}
</script>

<style scoped>
.pft-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

.pft-hero {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--v2-space-4);
    margin-bottom: var(--v2-space-5);
}

.pft-hero-text {
    min-width: 0;
}

.pft-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.pft-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
}

.pft-multi-list {
    flex-shrink: 0;
}

.pft-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
}

.pft-empty {
    text-align: center;
    padding: 64px 24px;
    background: var(--v2-surface-0);
    border: 1px dashed var(--v2-border);
    border-radius: var(--v2-radius-lg);
}

.pft-empty-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: var(--v2-surface-2);
    color: var(--v2-text-4);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.75rem;
    margin-bottom: var(--v2-space-4);
}

.pft-empty-title {
    font-size: 1.05rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    margin: 0 0 6px;
}

.pft-empty-text {
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    margin: 0 0 var(--v2-space-5);
}

.pft-empty-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 32px;
    padding: 0 16px;
    border-radius: var(--v2-radius-sm);
    background: var(--v2-primary);
    color: var(--v2-primary-contrast, #fff) !important;
    font-size: 0.82rem;
    font-weight: var(--v2-font-weight-medium);
    text-decoration: none !important;
    box-sizing: border-box;
    transition: background var(--v2-transition-fast);
}

.pft-empty-btn:hover {
    background: var(--v2-primary-hover);
}

.pft-empty-btn i {
    color: inherit !important;
    font-size: 0.8rem;
}

@media (max-width: 900px) {
    .pft-v2 {
        padding: 16px 18px 32px;
    }

    .pft-hero {
        flex-direction: column;
        align-items: stretch;
        gap: var(--v2-space-3);
    }

    .pft-hero-title {
        font-size: 1.375rem;
    }
}
</style>
